import React from 'react';
import {
  ArrowUpRight,
  Check,
  Store,
  Briefcase,
  ShoppingBag,
  CalendarDays,
  Zap,
  LifeBuoy,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import { PACKAGES, ALWAYS_INCLUDED } from '../mock';
import Reveal from './Reveal';

const ICONS = { Store, Briefcase, ShoppingBag, CalendarDays, Zap, LifeBuoy, ShieldCheck, Smartphone };

const WHATSAPP_NUMBER = '393383463712';
const buildWaUrl = (msg) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

export default function Packages() {
  const handleClick = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="pacchetti" className="relative py-24 md:py-32 overflow-hidden">
      {/* Strong color blobs so glass cards show through clearly */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -left-32 w-[620px] h-[620px] rounded-full bg-gradient-to-br from-[#bfdbfe] to-[#5eb0e8] blur-3xl opacity-80" />
        <div className="absolute top-[40%] left-[10%] w-[500px] h-[500px] rounded-full bg-[#1e6cb0]/45 blur-3xl" />
        <div className="absolute top-[55%] left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full bg-gradient-to-br from-[#5eb0e8]/55 to-[#1e6cb0]/35 blur-3xl" />
        <div className="absolute top-[35%] right-[5%] w-[520px] h-[520px] rounded-full bg-gradient-to-bl from-[#1e6cb0]/45 to-[#dbeafe]/70 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-[420px] h-[420px] rounded-full bg-[#5eb0e8]/35 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <Reveal variant="fade">
          <div className="flex items-start gap-3 mb-10">
            <span className="text-[#1e6cb0] font-bold text-[14px] tracking-[0.18em]">01</span>
            <span className="w-10 h-px mt-3 bg-[#1e6cb0]/60" />
            <span className="text-[12px] tracking-[0.2em] uppercase font-semibold text-neutral-500">
              Pacchetti
            </span>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <Reveal variant="up" delay={80} as="h2" className="lg:col-span-7 heading-mega text-[42px] sm:text-[56px] md:text-[72px] text-neutral-900">
            Pacchetti chiari.<br />
            <span className="text-[#1e6cb0]">Senza sorprese.</span>
          </Reveal>
          <Reveal variant="up" delay={220} as="p" className="lg:col-span-5 text-[16px] leading-relaxed text-neutral-600 lg:mt-6">
            Tre soluzioni, prezzo trasparente. Scegli mensile o annuale &mdash; quello vedi,
            quello paghi. Nessun costo nascosto, nessun preventivo che si gonfia strada facendo.
          </Reveal>
        </div>

        {/* Always included strip */}
        <Reveal variant="up" delay={120}>
          <div className="mb-12 p-5 md:p-6 rounded-3xl bg-white border border-black/5 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.1)]">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="text-[12px] tracking-[0.2em] uppercase font-bold text-[#1e6cb0] whitespace-nowrap">
                Sempre incluso
              </div>
              <div className="hidden md:block w-px h-8 bg-black/10" />
              <div className="flex flex-wrap gap-x-6 gap-y-3">
                {ALWAYS_INCLUDED.map((it) => {
                  const Icon = ICONS[it.icon];
                  return (
                    <div key={it.label} className="flex items-center gap-2 text-[14px] font-medium text-neutral-800">
                      <span className="w-7 h-7 rounded-lg bg-[#dbeafe] flex items-center justify-center">
                        <Icon className="w-3.5 h-3.5 text-[#1e6cb0]" />
                      </span>
                      {it.label}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Packages grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {PACKAGES.map((p, idx) => {
            const Icon = ICONS[p.icon];
            const featured = p.badge != null;
            return (
              <Reveal key={p.id} variant="up" delay={idx * 140} duration={820}>
              <article
                className={`group relative rounded-3xl p-7 card-hover overflow-hidden flex flex-col ${
                  featured
                    ? 'bg-gradient-to-br from-[#1e6cb0]/85 to-[#1857a0]/85 text-white border border-white/30 shadow-[0_24px_60px_-20px_rgba(30,108,176,0.55)]'
                    : 'bg-white/40 border border-white/70 shadow-[0_18px_50px_-20px_rgba(30,108,176,0.25)]'
                }`}
                style={{
                  WebkitBackdropFilter: 'blur(28px) saturate(180%)',
                  backdropFilter: 'blur(28px) saturate(180%)',
                }}
              >
                {p.badge && (
                  <span className="absolute -top-2.5 right-5 inline-flex items-center bg-white text-[#1e6cb0] text-[10px] font-bold tracking-[0.18em] uppercase px-2.5 py-1 rounded-full shadow">
                    {p.badge}
                  </span>
                )}

                <div className="flex items-start justify-between mb-5">
                  <div
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                      featured ? 'bg-white/15' : 'bg-[#dbeafe]'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${featured ? 'text-white' : 'text-[#1e6cb0]'}`} />
                  </div>
                </div>

                <h3
                  className={`font-display text-[22px] font-bold mb-1 ${
                    featured ? 'text-white' : 'text-neutral-900'
                  }`}
                >
                  {p.name}
                </h3>
                {p.sub && (
                  <div
                    className={`text-[12px] tracking-[0.12em] uppercase font-semibold mb-4 ${
                      featured ? 'text-white/70' : 'text-neutral-500'
                    }`}
                  >
                    {p.sub}
                  </div>
                )}
                <p
                  className={`text-[14px] leading-relaxed mb-6 ${
                    featured ? 'text-white/85' : 'text-neutral-600'
                  }`}
                >
                  {p.desc}
                </p>

                {/* Pricing */}
                <div className="space-y-2.5 mb-6">
                  <div
                    className={`rounded-xl px-4 py-3 ${
                      featured ? 'bg-white/10' : 'bg-neutral-50 border border-black/5'
                    }`}
                  >
                    <div className={`text-[11px] tracking-[0.16em] uppercase font-bold mb-1 ${featured ? 'text-white/70' : 'text-neutral-500'}`}>
                      Mensile
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span
                        className={`font-display text-[26px] font-extrabold leading-none ${
                          featured ? 'text-white' : 'text-neutral-900'
                        }`}
                      >
                        {p.monthly}
                      </span>
                      <span className={`text-[16px] font-bold ${featured ? 'text-white/85' : 'text-[#1e6cb0]'}`}>
                        &euro;
                      </span>
                      <span className={`text-[12px] ml-1 ${featured ? 'text-white/65' : 'text-neutral-500'}`}>
                        /mese
                      </span>
                    </div>
                  </div>
                  <div
                    className={`relative rounded-xl px-4 py-3 border-2 ${
                      featured
                        ? 'bg-white text-[#1e6cb0] border-white'
                        : 'bg-[#1e6cb0] text-white border-[#1e6cb0]'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-[11px] tracking-[0.16em] uppercase font-bold">Annuale</div>
                      <span
                        className={`text-[9px] font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded ${
                          featured ? 'bg-[#1e6cb0] text-white' : 'bg-white text-[#1e6cb0]'
                        }`}
                      >
                        Consigliato
                      </span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="font-display text-[28px] font-extrabold leading-none">{p.yearly}</span>
                      <span className="text-[17px] font-bold">&euro;</span>
                      <span className={`text-[12px] ml-1 ${featured ? 'text-[#1e6cb0]/70' : 'text-white/75'}`}>
                        /anno
                      </span>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-2 mb-6 flex-1">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className={`flex items-start gap-2 text-[13.5px] ${
                        featured ? 'text-white/90' : 'text-neutral-700'
                      }`}
                    >
                      <span
                        className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          featured ? 'bg-white/20' : 'bg-[#dbeafe]'
                        }`}
                      >
                        <Check
                          className={`w-2.5 h-2.5 ${featured ? 'text-white' : 'text-[#1e6cb0]'}`}
                          strokeWidth={3}
                        />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <a
                  href={buildWaUrl(
                    `Ciao! Mi interessa il pacchetto "${p.name}" (${p.monthly}€/mese o ${p.yearly}€/anno). Possiamo parlarne?`
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full font-semibold text-[14px] transition-all ${
                    featured
                      ? 'bg-white text-[#1e6cb0] hover:bg-neutral-100'
                      : 'bg-neutral-900 text-white hover:bg-[#1e6cb0]'
                  }`}
                >
                  Scrivimi su WhatsApp <ArrowUpRight className="w-4 h-4" />
                </a>
              </article>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-8 md:p-10 rounded-3xl bg-white border border-black/5 shadow-[0_20px_60px_-25px_rgba(0,0,0,0.12)]">
          <div>
            <div className="text-[12px] tracking-[0.2em] uppercase font-semibold text-[#1e6cb0] mb-3">
              Non sai quale fa per te?
            </div>
            <h3 className="heading-mega text-[26px] md:text-[34px] text-neutral-900 max-w-xl">
              Te lo diciamo noi nella call. Senza spingerti su quello che costa di pi&ugrave;.
            </h3>
          </div>
          <a
            href="https://wa.me/393383463712?text=Ciao!%20Non%20so%20quale%20pacchetto%20fa%20per%20me,%20possiamo%20parlarne?"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#1e6cb0] text-white px-7 py-4 rounded-full font-semibold btn-glow whitespace-nowrap"
          >
            Scrivimi su WhatsApp <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
